import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useBrokerList, usePVRange } from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { BrokerPeriod } from '@/schemas/broker'
import { pvRangeSearchSchema, toPVRangeQuery } from '@/screens/brokers/search'
import BrokerPeriodPicker from '@/screens/brokers/components/broker/BrokerPeriodPicker'
import StockPicker from '@/screens/brokers/components/broker/StockPicker'
import WindowNote from '@/screens/brokers/components/broker/WindowNote'
import type { BrokerChoice } from '@/screens/brokers/components/compare/AddBrokerMenu'
import BrokerSideFilter from '@/screens/brokers/components/pv-range/BrokerSideFilter'
import PVBucketChart from '@/screens/brokers/components/pv-range/PVBucketChart'
import { PVRangeSkeleton } from '@/screens/brokers/components/pv-range/PVRangeStates'
import PVSummary from '@/screens/brokers/components/pv-range/PVSummary'
import { PV_RANGE_BROKER_LIST_QUERY } from '@/screens/brokers/lib/pvRangeBrokerList'
import { loadPVRange } from '@/screens/brokers/PVRangeScreen.loader'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="PV Range" />

function PVRangePending() {
  const { symbol } = Route.useParams()
  return (
    <ScreenLayout
      pending
      pendingLabel={`Loading ${symbol}’s price-volume profile…`}
      header={HEADER}
      pageTitle="PV Range"
    >
      <PVRangeSkeleton />
    </ScreenLayout>
  )
}

/**
 * Light on purpose (docs/route-loading.md, "Error components"): only
 * `RouteErrorPage` plus the static header, so it stays in the entry chunk.
 * Neither `STOCK_NOT_FOUND`
 * nor `NO_FLOORSHEET_DATA` reaches here any more — `pvRangeQueryOptions`
 * resolves both to a named miss (`'not-found'` / `'no-floorsheet'`) instead
 * of throwing, and the screen's own body draws those branches (below). This
 * component only ever shows for a genuine failure: offline, or the request
 * itself broke.
 */
function PVRangeError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage {...props} header={HEADER} title="Couldn't load the price-volume profile" />
  )
}

export const Route = createFileRoute('/_app/brokers/pv-range/$symbol')({
  validateSearch: pvRangeSearchSchema,
  loader: loadPVRange,
  pendingComponent: PVRangePending,
  errorComponent: PVRangeError,
  component: PVRangeScreen,
})

/**
 * Brokers · PV range (V2 `4243:104586`) — where one stock's volume actually
 * traded, bucketed by price.
 *
 * BUILT LATE, AND FOR NO GOOD REASON. `routes/brokers.ts` carried a comment
 * saying this flow had no backend endpoint and was pending a decision. It was
 * wrong: `GET /api/v5/brokers/pv-range/{symbol}/` has been live in mm-be
 * (`services/broker/pvrange.py`, routed in `api/v5/brokers/urls.py`) and
 * present in this app's own generated `src/api/schema.d.ts` all along. That
 * comment is corrected rather than deleted, so the next reader can see which of
 * the trio really is blocked.
 *
 * Broker Analysis mock parity (2026-10-28): the section strip names the page, so the body opens
 * on the controls — Stock, Buyer, Seller on the left, the period and its calendar on the right —
 * then the as-of lede, the three-cell strip (Busiest range, Traded in that range, Traded
 * {period}) and the bar chart with no value axis, whose bars open a pinnable row card. No
 * footnote.
 *
 * The busiest range is the mock's PAIR of neighbouring Rs 1 rungs, derived client-side
 * (`busiestRange`); the endpoint's own `busiest` is the single heaviest bucket and the strip no
 * longer reads it.
 */
function PVRangeScreen() {
  const { symbol } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()

  // Through `toPVRangeQuery`, not `search` itself — see the picker's own
  // comment in search.ts for why the two are not interchangeable.
  const query = usePVRange(symbol, toPVRangeQuery(search))
  // `pvRangeQueryOptions` resolves a bad symbol or an unsettled floorsheet to
  // a named miss STRING rather than throwing (docs/route-loading.md,
  // "Not-found answers") — a `PVRange` is always an object, so `typeof` tells
  // the two apart cheaply. Checked below as its own branch, ahead of the
  // generic failed/offline one.
  const raw = query.data
  const miss = typeof raw === 'string' ? raw : undefined
  const data = typeof raw === 'string' ? undefined : raw
  // `data` on screen wins over a failed background refetch, and an offline
  // first load reads as offline rather than loading forever — see
  // `resolveViewState`'s own doc comment. A miss is not "loading" either, but
  // it is checked as its own branch below before `view` is ever read.
  const view = resolveViewState({ ...query, data }, { online })

  const brokerList = useBrokerList(PV_RANGE_BROKER_LIST_QUERY)
  const choices: readonly BrokerChoice[] = (brokerList.data?.rows ?? []).map((row) => ({
    code: row.code,
    name: row.name,
  }))

  /**
   * `keepPreviousData` holds `isLoading` FALSE across a period or filter change,
   * so without this the tiles and the chart would sit there showing the window
   * the member LEFT, unmarked, for the whole fetch. A refresh region rather
   * than blanked — they are real numbers, just the previous question's. The
   * same pair `ByStockScreen` and `LargeTradesScreen` use.
   */
  const isUpdating = query.isPlaceholderData

  const onPeriodChange = (next: BrokerPeriod) => {
    void navigate({ search: (prev) => ({ ...prev, period: next }) })
  }

  // The period carries over; the buyer and seller do not. A broker filtered
  // for one stock may never have traded the next, and a profile narrowed to
  // them would read as an empty stock rather than a stale filter.
  const onStockChange = (next: string) => {
    void navigate({
      to: '/brokers/pv-range/$symbol',
      params: { symbol: next },
      search: { period: search.period },
    })
  }

  return (
    <ScreenLayout header={HEADER} pageTitle="PV Range">
      {/* One row, as the mock draws it: the Stock, Buyer and Seller selects on the left,
          each a label over its trigger, the period control and its calendar on the right.
          Stock is 240 wide (the mock's 257, trimmed so the whole row fits the 844 main
          column); the searchable picker stays. The row wraps below that. The calendar opens on "Not available yet" until the endpoint takes a date. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <StockPicker
            symbol={symbol}
            name={data?.name}
            onPick={onStockChange}
            showLabel
            triggerClassName="w-60"
          />
          <BrokerSideFilter
            side="Buyer"
            choices={choices}
            value={search.buyer}
            onChange={(buyer) => {
              void navigate({ search: (prev) => ({ ...prev, buyer }) })
            }}
            isLoadingChoices={brokerList.isPending}
          />
          <BrokerSideFilter
            side="Seller"
            choices={choices}
            value={search.seller}
            onChange={(seller) => {
              void navigate({ search: (prev) => ({ ...prev, seller }) })
            }}
            isLoadingChoices={brokerList.isPending}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <UpdatingNote when={isUpdating} label="Updating this window…" />
          <BrokerPeriodPicker value={search.period} onChange={onPeriodChange} />
        </div>
      </div>

      {miss === 'not-found' ? (
        // Its own branch, above the tiles: a symbol that is not a listed company
        // has no profile to draw an empty chart of, and "try a longer period"
        // would be the wrong advice. Same split, same copy shape, as
        // ByStockScreen's.
        <EmptyState
          kind="not-found"
          size="page"
          title="We couldn't find that stock"
          description={`"${symbol}" doesn't match a listed company. Check the symbol and try again.`}
          action={
            <Link to="/market/stock-prices" className={stateActionClass('page')}>
              Browse stock prices
            </Link>
          }
        />
      ) : miss === 'no-floorsheet' ? (
        <EmptyState
          kind="empty"
          size="page"
          title="No floorsheet yet"
          description="PV range reads executed trades — it will fill in once a session has settled."
        />
      ) : view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          {...(view === 'failed' ? { title: "Couldn't load the price-volume profile" } : {})}
        />
      ) : (
        <RefreshRegion updating={isUpdating} className="flex flex-col gap-6">
          {/*
            The frame's lede wants "Updated 3:00 PM, 2 Bhadra 2083"; the wire's
            `as_of` is a DateField, so this prints the date and does not invent a
            clock — as Most traded and Large holdings also do.

            `source` is `floorsheet` here, a third value alongside
            `precomputed`/`live` (see `BROKER_SOURCES`). It means the profile
            read raw trades for every session in the window, so
            `isPartialWindow` is correctly false and `WindowNote` draws the
            plain as-of line rather than a partial-window warning.
          */}
          <WindowNote window={data?.window} />

          <PVSummary data={data} isLoading={query.isLoading} />

          <PVBucketChart
            buckets={data?.buckets ?? []}
            isLoading={query.isLoading}
            symbol={symbol}
          />
        </RefreshRegion>
      )}
    </ScreenLayout>
  )
}
